import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    Button,
    CardButton,
    IntlPluralize,
    intlPluralizeString,
    toast,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { OrganizationIcon, OrganizationPlanBadge, UserAvatarStack } from '../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../navigation';
import {
    apiQuery,
    getOnboardingParam,
    OnboardingParamName,
    useJoinOrganization,
} from '../../state';

/**
 * Screen to list organizations the user has been invited to join.
 */
export function OrganizationsListScreen({
    organizations,
    onClickCreateOrg,
    onNext,
}: {
    organizations: Array<api.Organization & { isMember: boolean }>;
    onClickCreateOrg: () => void;
    onNext: ({ organization }: { organization: api.Organization }) => Promise<void>;
}): React.ReactElement {
    useSetAnalyticsScreen({ screen: 'welcome_list_organizations' }, []);

    const [selectedOrg, setSelectedOrg] = React.useState<api.Organization | null>(null);
    const { navigate } = useNavigation();

    const onSelect = async (org: api.Organization) => {
        // If the OpenAPI URL param is present, let's continue with the onboarding flow
        // to create a site and import the OpenAPI spec (WelcomeScreen)
        if (getOnboardingParam(OnboardingParamName.OPENAPI)) {
            setSelectedOrg(org);
            await onNext({ organization: org });
            return;
        }

        navigate({
            screen: { type: 'root', organization: org.id },
        });
    };

    return (
        <>
            <div className="flex flex-col gap-4">
                <h2 className="text-ui-heading-large">Your organizations</h2>
                <p className="text-muted">You are a member of or can join these organizations.</p>
            </div>
            <OrganizationsList
                organizations={organizations}
                onSelect={onSelect}
                selectedOrg={selectedOrg}
            />
            <div className="flex">
                <Button
                    kind="secondary"
                    onPress={onClickCreateOrg}
                    label="or create a new organization"
                    size="large"
                    disabled={Boolean(selectedOrg)}
                />
            </div>
        </>
    );
}

function OrganizationsList({
    organizations,
    onSelect,
    selectedOrg,
}: {
    organizations: Array<api.Organization & { isMember: boolean }>;
    onSelect: (org: api.Organization) => Promise<void>;
    selectedOrg: api.Organization | null;
}): React.ReactElement {
    const [displayedOrgs, setDisplayedOrgs] = React.useState(3);

    // Split organizations: email domain orgs first, then member orgs
    const emailDomainOrgs = organizations.filter((org) => !org.isMember);
    const memberOrgs = organizations.filter((org) => org.isMember);
    const sortedOrgs = [...emailDomainOrgs, ...memberOrgs];

    const renderedOrgs = sortedOrgs.slice(0, displayedOrgs);
    const remainingOrgsToDisplay = sortedOrgs.length - renderedOrgs.length;
    const nextIncrease = Math.min(remainingOrgsToDisplay, 3);

    return (
        <div className="flex flex-col gap-4">
            <div className="-mx-4 flex max-h-[24rem] flex-col gap-4 overflow-x-visible overflow-y-auto p-4">
                {renderedOrgs.map(({ isMember, ...org }) => (
                    <OrganizationCard
                        key={org.id}
                        org={org}
                        isMember={isMember}
                        onSelect={onSelect}
                        disabled={Boolean(selectedOrg)}
                    />
                ))}
            </div>
            {sortedOrgs.length > renderedOrgs.length ? (
                <div className="flex justify-end">
                    <Button
                        label={`Show ${nextIncrease} more ${intlPluralizeString({
                            count: nextIncrease,
                            plural: 'organizations',
                            singular: 'organization',
                        })}`}
                        kind="blank"
                        size="small"
                        onPress={() => setDisplayedOrgs((prev) => prev + nextIncrease)}
                    />
                </div>
            ) : null}
        </div>
    );
}

function OrganizationCard({
    org,
    isMember,
    onSelect,
    disabled,
}: {
    org: api.Organization;
    isMember: boolean;
    onSelect: (org: api.Organization) => Promise<void>;
    disabled: boolean;
}): React.ReactElement | null {
    const { navigate } = useNavigation();
    const joinOrganization = useJoinOrganization();

    const handleJoinOrg = useEventCallback(async () => {
        await toast.catch(async () => {
            await joinOrganization.mutateAsync(org.id);

            toast.success(`You have joined "${org.title}"`);
            navigate({
                screen: { type: 'root', organization: org.id },
            });
        }, 'There was an error joining the organization');
    });

    return (
        <CardButton
            className={clsx('flex w-full grow justify-between p-6')}
            onPress={isMember ? () => onSelect(org) : undefined}
            disabled={disabled}
        >
            <div className="flex flex-1">
                <div className="mr-4 flex items-center">
                    <div
                        style={{
                            filter: 'drop-shadow(var(--sp-shadow-organization-creation-logo-first-layer)) drop-shadow(var(--sp-shadow-organization-creation-logo-second-layer))',
                        }}
                    >
                        <OrganizationIcon organization={org} size={48} />
                    </div>
                </div>
                <div className="flex flex-1 flex-col justify-center">
                    <div className="flex items-center gap-2 text-ui-heading-small">
                        {org.title}
                        <OrganizationPlanBadge
                            plan={org.plan}
                            trialing={org.trial.status === api.BillingTrialStatus.Active}
                        />
                    </div>

                    <React.Suspense fallback={<div className="h-6" />}>
                        {org.permissions.listMembers ? (
                            <MembersInfo organizationId={org.id} />
                        ) : null}
                    </React.Suspense>
                </div>
            </div>
            <div className="flex shrink-0 grow-0 items-center justify-center pl-4">
                {isMember ? (
                    <span>Member</span>
                ) : (
                    <Button kind="secondary" label="Join org" onPress={handleJoinOrg} loadable />
                )}
            </div>
        </CardButton>
    );
}

function MembersInfo(props: { organizationId: string }) {
    const { organizationId } = props;
    const { data: members } = useSuspenseQuery(
        apiQuery(
            'listMembersInOrganizationById',
            { organizationId, limit: 3, order: 'asc' },
            { nullOnError: true }
        )
    );

    if (!members) {
        return null;
    }

    const usersIds = members.items.map((item) => item.user.id);

    return (
        <div className="flex items-center gap-2">
            <div className="text-ui-small whitespace-nowrap text-muted">
                {members.count}{' '}
                <IntlPluralize count={members.count || 0} singular="member" plural="members" />
            </div>
            <UserAvatarStack users={usersIds} size="sm" />
        </div>
    );
}
